import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Button,
  Container,
  FormControl,
  InputLabel,
  Box,
  Modal,
  Tooltip,
  MenuItem,
  Select,
  TextField,
  Typography,
  Paper,
  RadioGroup,
  FormControlLabel,
  Radio,
  IconButton,
  useTheme,
  Snackbar,
} from '@mui/material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import { styled, alpha, getContrastRatio } from '@mui/material/styles';
import MuiAlert from '@mui/material/Alert';
import {
  AttachFile as AttachFileIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { decimalOnly } from '../../../utils/numericInput';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  'https://wims.revisetoexcel.com';

const Alert = React.forwardRef(function Alert(props, ref) {
  return (
    <MuiAlert
      elevation={6}
      ref={ref}
      variant="filled"
      {...props}
    />
  );
});

const CustomTableScrollbar = styled('div')(({ theme }) => ({
  overflowY: 'auto',
  backgroundColor: theme.palette.background.paper,
  '&::-webkit-scrollbar': {
    width: '6px',
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,0.2)'
        : 'rgba(0,0,0,0.2)',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,0.3)'
        : 'rgba(0,0,0,0.3)',
  },
  scrollbarWidth: 'thin',
  scrollbarColor:
    theme.palette.mode === 'dark'
      ? 'rgba(255,255,255,0.2) transparent'
      : 'rgba(0,0,0,0.2) transparent',
}));

const EMPTY_FORM_DATA = {
  employeeCode: '',
  employeeName: '',
  dateOfBirth: '',
  dateOfJoining: '',
  gender: '',
  mobileNo: '',
  alternateNo: '',
  email: '',
  department: '',
  designation: '',
  country: '',
  state: '',
  city: '',
  maritalStatus: '',
  organization: '',
  branch: '',
  address: '',
  salary: '',
};

const MARITAL_STATUS = [
  { statusId: 1, statusName: 'single' },
  { statusId: 2, statusName: 'married' },
];

const toStringValue = (value) => {
  if (value === null || value === undefined) return '';
  return String(value);
};

const toNumericId = (value) => {
  if (value === '' || value === null || value === undefined) return '';
  const numberValue = Number(value);
  return Number.isNaN(numberValue) ? value : numberValue;
};

const getAuthToken = () =>
  localStorage.getItem('accessToken') ||
  localStorage.getItem('token') ||
  sessionStorage.getItem('accessToken') ||
  sessionStorage.getItem('token') ||
  sessionStorage.getItem('accountEntryToken');

const safeJsonParse = (value, fallback = {}) => {
  try {
    return JSON.parse(value) || fallback;
  } catch {
    return fallback;
  }
};

const resolveMediaUrl = (path) => {
  if (!path) return null;

  // Already absolute (e.g. blob: preview or full http(s) URL) — leave as-is
  if (/^(https?:|blob:|data:)/i.test(path)) {
    return path;
  }

  // Avoid double slashes when joining base + path
  const base = API_BASE_URL.replace(/\/+$/, '');
  const cleanPath = String(path).startsWith('/') ? path : `/${path}`;

  return `${base}${cleanPath}`;
};

const EditEmployee = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { employeeID } = useParams();

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [showSuccess, setShowSuccess] = useState(false);
  const [loadingEmployee, setLoadingEmployee] = useState(true);
  const [saving, setSaving] = useState(false);

  const [profilePhoto, setProfilePhoto] = useState(null);
  const [resume, setResume] = useState(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState(null);
  const [resumeFileName, setResumeFileName] = useState('');

  const [organizations, setOrganizations] = useState([]);
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [countries, setCountries] = useState([]);
  const [allStates, setAllStates] = useState([]);
  const [allCities, setAllCities] = useState([]);

  const [filteredBranches, setFilteredBranches] = useState([]);
  const [filteredDesignations, setFilteredDesignations] = useState([]);
  const [filteredStates, setFilteredStates] = useState([]);
  const [filteredCities, setFilteredCities] = useState([]);

  const [formData, setFormData] = useState(EMPTY_FORM_DATA);

  const MAX_FILE_SIZE = 1 * 1024 * 1024;

  const showMessage = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  // ------------------------------------------------------------
  // Organizations
  // ------------------------------------------------------------
  useEffect(() => {
    let active = true;

    const fetchOrganizations = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/master/organizations/`,
          {
            headers: {
              Authorization: `Bearer ${getAuthToken()}`,
            },
          }
        );

        if (!active) return;

        const list = Array.isArray(response.data) ? response.data : [];

        setOrganizations(
          list
            .filter(
              (org) =>
                org &&
                (org.id !== undefined ||
                  org.OrganizationId !== undefined)
            )
            .map((org) => ({
              OrganizationId: toNumericId(
                org.id ?? org.OrganizationId
              ),
              OrganizationName: toStringValue(
                org.name ?? org.OrganizationName
              ),
              email: org.email ?? '',
              mobile: org.mobile ?? '',
            }))
        );
      } catch (error) {
        console.error('Error fetching organizations:', error);
        if (active) {
          setOrganizations([]);
          showMessage('Failed to load organizations', 'error');
        }
      }
    };

    fetchOrganizations();

    return () => {
      active = false;
    };
  }, []);

  // ------------------------------------------------------------
  // Branches - safely resolve logged-in organization
  // ------------------------------------------------------------
  useEffect(() => {
    let active = true;

    const fetchBranches = async () => {
      try {
        const user = safeJsonParse(
          sessionStorage.getItem('user') ||
            localStorage.getItem('user') ||
            '{}'
        );

        const organizationId =
          user?.organization_id ??
          user?.organizationId ??
          user?.organization?.id ??
          sessionStorage.getItem('organization_id') ??
          localStorage.getItem('organization_id') ??
          sessionStorage.getItem('organizationId') ??
          localStorage.getItem('organizationId');

        if (
          organizationId === undefined ||
          organizationId === null ||
          organizationId === ''
        ) {
          console.warn('Organization ID not found while loading branches');
          if (active) setBranches([]);
          return;
        }

        const normalizedOrgId = toNumericId(organizationId);

        const response = await axios.get(
          `${API_BASE_URL}/api/master/organization/${normalizedOrgId}/branches/`,
          {
            params: {
              organization_id: normalizedOrgId,
            },
            headers: {
              Authorization: `Bearer ${getAuthToken()}`,
            },
          }
        );

        if (!active) return;

        const list = Array.isArray(response.data) ? response.data : [];

        setBranches(
          list
            .filter((branch) => branch && branch.id !== undefined)
            .map((branch) => ({
              branchId: toNumericId(branch.id),
              branchName: toStringValue(
                branch.name ?? branch.branch_name
              ),
              organizationId: toNumericId(
                branch.organization ?? branch.organization_id
              ),
            }))
        );
      } catch (error) {
        console.error('Error fetching branches:', error);
        if (active) setBranches([]);
      }
    };

    fetchBranches();

    return () => {
      active = false;
    };
  }, []);

  // ------------------------------------------------------------
  // Departments
  // ------------------------------------------------------------
  useEffect(() => {
    let active = true;

    const fetchDepartments = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/master/departments/`,
          {
            headers: {
              Authorization: `Bearer ${getAuthToken()}`,
            },
          }
        );

        if (!active) return;

        const list = Array.isArray(response.data) ? response.data : [];

        setDepartments(
          list
            .filter((dept) => dept && dept.id !== undefined)
            .map((dept) => ({
              departmentId: toNumericId(dept.id),
              departmentName: toStringValue(dept.name),
            }))
        );
      } catch (error) {
        console.error('Error fetching departments:', error);
        if (active) setDepartments([]);
      }
    };

    fetchDepartments();

    return () => {
      active = false;
    };
  }, []);

  // ------------------------------------------------------------
  // Designations
  // ------------------------------------------------------------
  useEffect(() => {
    let active = true;

    const fetchDesignations = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/master/designations/`,
          {
            headers: {
              Authorization: `Bearer ${getAuthToken()}`,
            },
          }
        );

        if (!active) return;

        const list = Array.isArray(response.data) ? response.data : [];

        setDesignations(
          list
            .filter((desig) => desig && desig.id !== undefined)
            .map((desig) => ({
              designationId: toNumericId(desig.id),
              designationName: toStringValue(desig.name),
              departmentId: toNumericId(
                desig.department ?? desig.department_id
              ),
            }))
        );
      } catch (error) {
        console.error('Error fetching designations:', error);
        if (active) setDesignations([]);
      }
    };

    fetchDesignations();

    return () => {
      active = false;
    };
  }, []);

  // ------------------------------------------------------------
  // Countries
  // ------------------------------------------------------------
  useEffect(() => {
    let active = true;

    const fetchCountries = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/master/countries/`,
          {
            headers: {
              Authorization: `Bearer ${getAuthToken()}`,
            },
          }
        );

        if (!active) return;

        const list = Array.isArray(response.data) ? response.data : [];

        setCountries(
          list
            .filter((country) => country && country.id !== undefined)
            .map((country) => ({
              countryId: toNumericId(country.id),
              countryName: toStringValue(country.name),
            }))
        );
      } catch (error) {
        console.error('Error fetching countries:', error);
        if (active) setCountries([]);
      }
    };

    fetchCountries();

    return () => {
      active = false;
    };
  }, []);

  // ------------------------------------------------------------
  // States
  // ------------------------------------------------------------
  useEffect(() => {
    let active = true;

    const fetchStates = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/master/states/`,
          {
            headers: {
              Authorization: `Bearer ${getAuthToken()}`,
            },
          }
        );

        if (!active) return;

        const list = Array.isArray(response.data) ? response.data : [];

        setAllStates(
          list
            .filter((state) => state && state.id !== undefined)
            .map((state) => ({
              stateId: toNumericId(state.id),
              stateName: toStringValue(state.name),
              countryId: toNumericId(
                state.country ?? state.country_id
              ),
            }))
        );
      } catch (error) {
        console.error('Error fetching states:', error);
        if (active) setAllStates([]);
      }
    };

    fetchStates();

    return () => {
      active = false;
    };
  }, []);

  // ------------------------------------------------------------
  // Cities
  // ------------------------------------------------------------
  useEffect(() => {
    let active = true;

    const fetchCities = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/master/cities/`,
          {
            headers: {
              Authorization: `Bearer ${getAuthToken()}`,
            },
          }
        );

        if (!active) return;

        const list = Array.isArray(response.data) ? response.data : [];

        setAllCities(
          list
            .filter((city) => city && city.id !== undefined)
            .map((city) => ({
              cityId: toNumericId(city.id),
              cityName: toStringValue(city.name),
              stateId: toNumericId(
                city.state ?? city.state_id
              ),
            }))
        );
      } catch (error) {
        console.error('Error fetching cities:', error);
        if (active) setAllCities([]);
      }
    };

    fetchCities();

    return () => {
      active = false;
    };
  }, []);

  // ------------------------------------------------------------
  // Filter branches by organization ID
  // ------------------------------------------------------------
  useEffect(() => {
    const organizationId = formData.organization;

    if (
      organizationId === '' ||
      organizationId === null ||
      organizationId === undefined
    ) {
      setFilteredBranches([]);
      return;
    }

    const result = branches.filter(
      (branch) =>
        String(branch.organizationId) === String(organizationId)
    );

    setFilteredBranches(result);

    if (
      result.length === 1 &&
      String(formData.branch || '') === ''
    ) {
      setFormData((prev) => ({
        ...prev,
        branch: toStringValue(result[0].branchId),
      }));
    }
  }, [formData.organization, branches]);

  // ------------------------------------------------------------
  // Filter designations by department ID
  // ------------------------------------------------------------
  useEffect(() => {
    const departmentId = formData.department;

    if (
      departmentId === '' ||
      departmentId === null ||
      departmentId === undefined
    ) {
      setFilteredDesignations([]);
      return;
    }

    setFilteredDesignations(
      designations.filter(
        (designation) =>
          String(designation.departmentId) === String(departmentId)
      )
    );
  }, [formData.department, designations]);

  // ------------------------------------------------------------
  // Filter states by country ID
  // ------------------------------------------------------------
  useEffect(() => {
    const countryId = formData.country;

    if (
      countryId === '' ||
      countryId === null ||
      countryId === undefined
    ) {
      setFilteredStates([]);
      return;
    }

    setFilteredStates(
      allStates.filter(
        (state) =>
          String(state.countryId) === String(countryId)
      )
    );
  }, [formData.country, allStates]);

  // ------------------------------------------------------------
  // Filter cities by state ID
  // ------------------------------------------------------------
  useEffect(() => {
    const stateId = formData.state;

    if (
      stateId === '' ||
      stateId === null ||
      stateId === undefined
    ) {
      setFilteredCities([]);
      return;
    }

    setFilteredCities(
      allCities.filter(
        (city) =>
          String(city.stateId) === String(stateId)
      )
    );
  }, [formData.state, allCities]);

  // ------------------------------------------------------------
  // Normalize employee object coming from API
  // ------------------------------------------------------------
  const normalizeEmployeeData = (employeeData) => ({
    employeeCode: toStringValue(employeeData?.employee_code),
    employeeName: toStringValue(employeeData?.name),
    dateOfBirth: toStringValue(employeeData?.dob),
    dateOfJoining: toStringValue(
      employeeData?.date_of_joining
    ),
    gender: toStringValue(employeeData?.gender),
    mobileNo: toStringValue(employeeData?.mobile),
    alternateNo: toStringValue(employeeData?.phone),
    email: toStringValue(employeeData?.email),

    // IMPORTANT: store IDs, not names
    department: toStringValue(employeeData?.department),
    designation: toStringValue(employeeData?.designation),
    country: toStringValue(employeeData?.country),
    state: toStringValue(employeeData?.state),
    city: toStringValue(employeeData?.city),
    organization: toStringValue(employeeData?.organization),
    branch: toStringValue(employeeData?.branch),

    maritalStatus: toStringValue(
      employeeData?.marital_status
    ),
    address: toStringValue(employeeData?.address),
    salary: toStringValue(employeeData?.salary),
  });

  // ------------------------------------------------------------
  // Fetch employee data
  // ------------------------------------------------------------
  useEffect(() => {
    let active = true;

    const loadEmployee = async () => {
      if (!employeeID) {
        setLoadingEmployee(false);
        return;
      }

      try {
        setLoadingEmployee(true);

        const response = await axios.get(
          `${API_BASE_URL}/api/staff/employees/${employeeID}/`,
          {
            headers: {
              Authorization: `Bearer ${getAuthToken()}`,
            },
          }
        );

        if (!active) return;

        const employeeData = response.data;
        console.log('Employee data:', employeeData);

        setFormData(normalizeEmployeeData(employeeData));

        if (employeeData?.profile_photo) {
          setProfilePhotoPreview(
            resolveMediaUrl(employeeData.profile_photo)
          );
        }

        if (employeeData?.resume) {
          setResumeFileName(
            String(employeeData.resume)
              .split('/')
              .pop() || ''
          );
        }
      } catch (error) {
        console.error(
          'Error fetching employee data:',
          error
        );

        if (active) {
          showMessage(
            'Failed to fetch employee data',
            'error'
          );
        }
      } finally {
        if (active) {
          setLoadingEmployee(false);
        }
      }
    };

    loadEmployee();

    return () => {
      active = false;
    };
  }, [employeeID]);

  // ------------------------------------------------------------
  // Handle employeeData passed through router state
  // Only use it as fallback, and normalize it.
  // ------------------------------------------------------------
  useEffect(() => {
    const employeeData =
      location.state?.employeeData;

    if (!employeeData || employeeID) {
      return;
    }

    setFormData(
      normalizeEmployeeData(employeeData)
    );

    if (employeeData?.profile_photo) {
      setProfilePhotoPreview(
        resolveMediaUrl(employeeData.profile_photo)
      );
    }

    if (employeeData?.resume) {
      setResumeFileName(
        String(employeeData.resume)
          .split('/')
          .pop() || ''
      );
    }

    setLoadingEmployee(false);
  }, [location.state?.employeeData, employeeID]);

  // ------------------------------------------------------------
  // Safe change handler
  // ------------------------------------------------------------
  const handleChange = (event) => {
    if (!event?.target) {
      return;
    }

    const name = event.target.name;

    if (!name) {
      return;
    }

    const rawValue =
      event.target.value === undefined ||
      event.target.value === null
        ? ''
        : event.target.value;

    const selectFields = new Set([
      'organization',
      'branch',
      'department',
      'designation',
      'country',
      'state',
      'city',
    ]);

    const value = name === 'salary'
      ? decimalOnly(rawValue)
      : selectFields.has(name)
        ? String(rawValue)
        : rawValue;

    setFormData((prev) => {
      const next = {
        ...prev,
        [name]: value,
      };

      if (name === 'organization') {
        next.branch = '';
      }

      if (name === 'department') {
        next.designation = '';
      }

      if (name === 'country') {
        next.state = '';
        next.city = '';
      }

      if (name === 'state') {
        next.city = '';
      }

      return next;
    });
  };

  // ------------------------------------------------------------
  // Profile photo
  // ------------------------------------------------------------
  const handleProfilePhotoChange = (event) => {
    const file = event?.target?.files?.[0];

    if (!file) {
      return;
    }

    const validTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
    ];

    if (!validTypes.includes(file.type)) {
      showMessage(
        'Please upload a valid image file (JPG, JPEG, or PNG)',
        'error'
      );

      if (event?.target) {
        event.target.value = '';
      }

      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      showMessage(
        'Image size exceeds 1MB limit. Please choose a smaller image.',
        'error'
      );

      if (event?.target) {
        event.target.value = '';
      }

      return;
    }

    setProfilePhoto(file);

    const previewUrl = URL.createObjectURL(file);
    setProfilePhotoPreview(previewUrl);

    if (event?.target) {
      event.target.value = '';
    }
  };

  // ------------------------------------------------------------
  // Resume
  // ------------------------------------------------------------
  const handleResumeChange = (event) => {
    const file = event?.target?.files?.[0];

    if (!file) {
      return;
    }

    const validTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];

    if (!validTypes.includes(file.type)) {
      showMessage(
        'Please upload a valid resume file (PDF or DOCX)',
        'error'
      );

      if (event?.target) {
        event.target.value = '';
      }

      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      showMessage(
        'Resume size exceeds 1MB limit. Please choose a smaller file.',
        'error'
      );

      if (event?.target) {
        event.target.value = '';
      }

      return;
    }

    setResume(file);
    setResumeFileName(file.name);

    if (event?.target) {
      event.target.value = '';
    }
  };

  const handleRemoveProfilePhoto = () => {
    setProfilePhoto(null);
    setProfilePhotoPreview(null);
  };

  const handleRemoveResume = () => {
    setResume(null);
    setResumeFileName('');
  };

  // ------------------------------------------------------------
  // Save
  // ------------------------------------------------------------
  const handleSave = async (event) => {
    event.preventDefault();

    const requiredFields = [
      formData.employeeCode,
      formData.employeeName,
      formData.dateOfBirth,
      formData.dateOfJoining,
      formData.gender,
      formData.mobileNo,
      formData.email,
      formData.department,
      formData.designation,
      formData.country,
      formData.state,
      formData.city,
      formData.maritalStatus,
      formData.organization,
      formData.branch,
    ];

    if (requiredFields.some((value) => !value)) {
      showMessage(
        'Please fill all required fields!',
        'error'
      );
      return;
    }

    const employeeId =
      employeeID ||
      location.state?.employeeData?.id;

    if (!employeeId) {
      showMessage(
        'Employee ID not found',
        'error'
      );
      return;
    }

    setSaving(true);

    try {
      const payload = new FormData();

      payload.append(
        'name',
        String(formData.employeeName || '')
      );

      payload.append(
        'employee_code',
        String(formData.employeeCode || '')
      );

      payload.append(
        'address',
        String(formData.address || '')
      );

      payload.append(
        'dob',
        String(formData.dateOfBirth || '')
      );

      payload.append(
        'date_of_joining',
        String(formData.dateOfJoining || '')
      );

      payload.append(
        'mobile',
        String(formData.mobileNo || '')
      );

      payload.append(
        'gender',
        String(formData.gender || '')
      );

      payload.append(
        'email',
        String(formData.email || '')
      );

      payload.append(
        'phone',
        String(formData.alternateNo || '')
      );

      payload.append(
        'marital_status',
        String(
          formData.maritalStatus || ''
        ).toLowerCase()
      );

      payload.append(
        'salary',
        String(formData.salary || '0')
      );

      // IDs are sent directly
      payload.append(
        'organization',
        String(formData.organization)
      );

      payload.append(
        'branch',
        String(formData.branch)
      );

      payload.append(
        'department',
        String(formData.department)
      );

      payload.append(
        'designation',
        String(formData.designation)
      );

      payload.append(
        'country',
        String(formData.country)
      );

      payload.append(
        'state',
        String(formData.state)
      );

      payload.append(
        'city',
        String(formData.city)
      );

      if (profilePhoto) {
        payload.append(
          'profile_photo',
          profilePhoto
        );
      }

      if (resume) {
        payload.append(
          'resume',
          resume
        );
      }

      const response = await fetch(
        `${API_BASE_URL}/api/staff/employees/${employeeId}/`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${getAuthToken()}`,
          },
          body: payload,
        }
      );

      let responseData = null;

      const contentType =
        response.headers.get('content-type') || '';

      if (contentType.includes('application/json')) {
        responseData = await response.json();
      } else {
        const text = await response.text();
        responseData = text
          ? { message: text }
          : null;
      }

      if (!response.ok) {
        const backendMessage =
          responseData?.detail ||
          responseData?.message ||
          responseData?.error ||
          'Failed to update employee';

        throw new Error(backendMessage);
      }

      console.log(
        'Update successful:',
        responseData
      );

      setShowSuccess(true);
    } catch (error) {
      console.error(
        'Error updating employee:',
        error
      );

      showMessage(
        error?.message ||
          'Failed to update employee',
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const organizationOptions = useMemo(
    () => organizations || [],
    [organizations]
  );

  const branchOptions = useMemo(
    () => filteredBranches || [],
    [filteredBranches]
  );

  const departmentOptions = useMemo(
    () => departments || [],
    [departments]
  );

  const designationOptions = useMemo(
    () => filteredDesignations || [],
    [filteredDesignations]
  );

  const countryOptions = useMemo(
    () => countries || [],
    [countries]
  );

  const stateOptions = useMemo(
    () => filteredStates || [],
    [filteredStates]
  );

  const cityOptions = useMemo(
    () => filteredCities || [],
    [filteredCities]
  );

  const canSubmit =
    Boolean(formData.employeeCode) &&
    Boolean(formData.employeeName) &&
    Boolean(formData.dateOfBirth) &&
    Boolean(formData.dateOfJoining) &&
    Boolean(formData.gender) &&
    Boolean(formData.mobileNo) &&
    Boolean(formData.email) &&
    Boolean(formData.department) &&
    Boolean(formData.designation) &&
    Boolean(formData.country) &&
    Boolean(formData.state) &&
    Boolean(formData.city) &&
    Boolean(formData.maritalStatus) &&
    Boolean(formData.organization) &&
    Boolean(formData.branch);

  const submitTooltip = !canSubmit
    ? !formData.employeeCode
      ? 'Employee Code is required'
      : !formData.employeeName
      ? 'Employee Name is required'
      : !formData.dateOfBirth
      ? 'Date of Birth is required'
      : !formData.dateOfJoining
      ? 'Date of Joining is required'
      : !formData.gender
      ? 'Select Gender'
      : !formData.mobileNo
      ? 'Mobile No. is required'
      : !formData.email
      ? 'Email is required'
      : !formData.organization
      ? 'Select Organization'
      : !formData.branch
      ? 'Select Branch'
      : !formData.department
      ? 'Select Department'
      : !formData.designation
      ? 'Select Designation'
      : !formData.country
      ? 'Select Country'
      : !formData.state
      ? 'Select State'
      : !formData.city
      ? 'Select City'
      : !formData.maritalStatus
      ? 'Select Marital Status'
      : 'Complete required fields'
    : '';

  // ------------------------------------------------------------
  // FIX:
  // MUI getContrastRatio/getContrastText do not accept CSS named
  // colors such as "white". Normalize it to a valid hex value.
  // ------------------------------------------------------------
  const bgColor = theme.palette.background.paper;

  const normalizedBgColor =
    typeof bgColor === 'string' &&
    bgColor.toLowerCase() === 'white'
      ? '#ffffff'
      : bgColor;

  const isDarkBg =
    getContrastRatio(
      normalizedBgColor,
      '#ffffff'
    ) > 4.5;

  const readableColor =
    theme.palette.getContrastText(
      normalizedBgColor
    );

  const formSx = {
    // Labels (normal, focused, disabled, error)
    '&& .MuiInputLabel-root': {
      color: alpha(readableColor, 0.75),
    },

    '&& .MuiInputLabel-root.Mui-focused': {
      color: readableColor,
    },

    '&& .MuiInputLabel-root.Mui-disabled': {
      color: alpha(readableColor, 0.6),
    },

    '&& .MuiInputLabel-root.Mui-error': {
      color: theme.palette.error.main,
    },

    '&& .MuiInputLabel-asterisk': {
      color: theme.palette.error.main,
    },

    // Borders
    '&& .MuiOutlinedInput-notchedOutline': {
      borderColor: alpha(readableColor, 0.35),
    },

    '&& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: alpha(readableColor, 0.6),
    },

    '&& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: readableColor,
    },

    // Typed / selected text
    '&& .MuiOutlinedInput-input, && .MuiSelect-select': {
      color: readableColor,
    },

    '&& .MuiInputBase-input.Mui-disabled': {
      WebkitTextFillColor: alpha(
        readableColor,
        0.6
      ),
    },

    // Dropdown arrow icon
    '&& .MuiSelect-icon': {
      color: alpha(readableColor, 0.75),
    },

    // Calendar icon on date fields
    '&& input[type="date"]': {
      colorScheme: isDarkBg
        ? 'dark'
        : 'light',
    },

    '&& input[type="date"]::-webkit-calendar-picker-indicator': {
      cursor: 'pointer',
      opacity: 1,
      filter: isDarkBg
        ? 'invert(1)'
        : 'none',
    },
  };

  if (loadingEmployee) {
    return (
      <Container maxWidth="lg" sx={{ my: 0 }}>
        <Box
          sx={{
            p: 4,
            borderRadius: 2,
            backgroundColor:
              theme.palette.background.paper,
          }}
        >
          <Typography>
            Loading employee details...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ my: 0 }}>
      <Box
        sx={{
          p: 4,
          borderRadius: 2,
          backgroundColor:
            theme.palette.background.paper,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 'bold',
              color:
                theme.palette.primary.main,
            }}
          >
            Employee Details
          </Typography>
        </Box>

        {showSuccess ? (
          <Modal
            open={showSuccess}
            onClose={() => setShowSuccess(false)}
            BackdropProps={{
              style: {
                backgroundColor:
                  'rgba(0, 0, 0, 0.8)',
              },
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform:
                  'translate(-50%, -50%)',
                width: 400,
                bgcolor:
                  theme.palette.background.paper,
                borderRadius: 2,
                boxShadow: 24,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  backgroundColor:
                    theme.palette.primary.main,
                  padding: '12px 16px',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color:
                      theme.palette.primary
                        .contrastText,
                  }}
                >
                  Employee Updated ✅
                </Typography>
              </Box>

              <Box sx={{ padding: '20px' }}>
                <Typography sx={{ mb: 2 }}>
                  Employee details have been
                  updated successfully.
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent:
                      'flex-end',
                    mt: 3,
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      setShowSuccess(false);
                      navigate(-1);
                    }}
                  >
                    OK
                  </Button>
                </Box>
              </Box>
            </Box>
          </Modal>
        ) : (
          <Box
            component="form"
            onSubmit={handleSave}
            sx={formSx}
          >
            {/* Profile photo */}
            <Box
              sx={{
                mb: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Button
                variant="contained"
                component="label"
                startIcon={
                  <AttachFileIcon />
                }
              >
                Choose Image

                <input
                  type="file"
                  hidden
                  accept="image/jpeg,image/jpg,image/png"
                  onChange={
                    handleProfilePhotoChange
                  }
                />
              </Button>

              {profilePhotoPreview && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <img
                    src={profilePhotoPreview}
                    alt="Profile Preview"
                    style={{
                      maxWidth: '100px',
                      maxHeight: '100px',
                      objectFit: 'cover',
                      borderRadius: '4px',
                    }}
                  />

                  <IconButton
                    onClick={
                      handleRemoveProfilePhoto
                    }
                    size="small"
                    color="error"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>

            <Typography
              variant="caption"
              sx={{
                color:
                  theme.palette.text.primary,
                fontWeight: 500,
                ml: 1,
                display: 'block',
                mb: 2,
              }}
            >
              Maximum file size: 1MB
            </Typography>

            <TextField
              disabled
              label="Employee Code"
              variant="outlined"
              fullWidth
              name="employeeCode"
              value={
                formData.employeeCode ?? ''
              }
              onChange={handleChange}
              sx={{ mb: 3 }}
            />

            <TextField
              label="Employee Name"
              variant="outlined"
              fullWidth
              name="employeeName"
              value={
                formData.employeeName ?? ''
              }
              onChange={handleChange}
              inputProps={{ inputMode: 'decimal', maxLength: 12 }}
              sx={{ mb: 3 }}
            />

            <TextField
              label="Date of Birth"
              type="date"
              variant="outlined"
              fullWidth
              name="dateOfBirth"
              value={
                formData.dateOfBirth ?? ''
              }
              onChange={handleChange}
              InputLabelProps={{
                shrink: true,
              }}
              sx={{ mb: 3 }}
            />

            <TextField
              label="Date of Joining"
              type="date"
              variant="outlined"
              fullWidth
              name="dateOfJoining"
              value={
                formData.dateOfJoining ?? ''
              }
              onChange={handleChange}
              InputLabelProps={{
                shrink: true,
              }}
              sx={{ mb: 3 }}
            />

            <FormControl
              component="fieldset"
              sx={{ mb: 3 }}
            >
              <Typography
                component="legend"
                sx={{
                  color:
                    theme.palette.text.primary,
                }}
              >
                Gender
              </Typography>

              <RadioGroup
                row
                name="gender"
                value={
                  formData.gender ?? ''
                }
                onChange={handleChange}
              >
                <FormControlLabel
                  value="male"
                  control={
                    <Radio
                      sx={{
                        color:
                          theme.palette.text
                            .primary,
                        '&.Mui-checked': {
                          color:
                            theme.palette
                              .primary.main,
                        },
                      }}
                    />
                  }
                  label="Male"
                />

                <FormControlLabel
                  value="female"
                  control={
                    <Radio
                      sx={{
                        color:
                          theme.palette.text
                            .primary,
                        '&.Mui-checked': {
                          color:
                            theme.palette
                              .primary.main,
                        },
                      }}
                    />
                  }
                  label="Female"
                />
              </RadioGroup>
            </FormControl>

            <TextField
              type="tel"
              inputProps={{
                maxLength: 10,
              }}
              label="Mobile No."
              variant="outlined"
              fullWidth
              name="mobileNo"
              value={
                formData.mobileNo ?? ''
              }
              onChange={handleChange}
              sx={{ mb: 3 }}
            />

            <TextField
              type="tel"
              inputProps={{
                maxLength: 10,
              }}
              label="Alternate No."
              variant="outlined"
              fullWidth
              name="alternateNo"
              value={
                formData.alternateNo ?? ''
              }
              onChange={handleChange}
              sx={{ mb: 3 }}
            />

            <TextField
              label="Email"
              variant="outlined"
              fullWidth
              name="email"
              value={
                formData.email ?? ''
              }
              onChange={handleChange}
              sx={{ mb: 3 }}
            />

            <TextField
              label="Salary"
              variant="outlined"
              fullWidth
              name="salary"
              value={
                formData.salary ?? ''
              }
              onChange={handleChange}
              inputProps={{ inputMode: 'decimal', maxLength: 12 }}
              sx={{ mb: 3 }}
            />

            {/* Department */}
            <FormControl
              fullWidth
              sx={{ mb: 3 }}
              required
            >
              <InputLabel id="department-label">
                Department
              </InputLabel>

              <Select
                labelId="department-label"
                name="department"
                value={
                  formData.department ?? ''
                }
                label="Department"
                onChange={handleChange}
                required
              >
                <MenuItem value="">
                  <em>
                    Select Department
                  </em>
                </MenuItem>

                {departmentOptions
                  .filter(Boolean)
                  .map((dept) => (
                    <MenuItem
                      key={String(
                        dept.departmentId
                      )}
                      value={String(
                        dept.departmentId
                      )}
                    >
                      {
                        dept.departmentName
                      }
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            {/* Designation */}
            <FormControl
              fullWidth
              sx={{ mb: 3 }}
              required
            >
              <InputLabel id="designation-label">
                Designation
              </InputLabel>

              <Select
                labelId="designation-label"
                name="designation"
                value={
                  formData.designation ?? ''
                }
                label="Designation"
                onChange={handleChange}
                required
                disabled={
                  !formData.department
                }
              >
                <MenuItem value="">
                  <em>
                    Select Designation
                  </em>
                </MenuItem>

                {designationOptions
                  .filter(Boolean)
                  .map((designation) => (
                    <MenuItem
                      key={String(
                        designation.designationId
                      )}
                      value={String(
                        designation.designationId
                      )}
                    >
                      {
                        designation.designationName
                      }
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            {/* Country */}
            <FormControl
              fullWidth
              sx={{ mb: 3 }}
              required
            >
              <InputLabel id="country-label">
                Country
              </InputLabel>

              <Select
                labelId="country-label"
                name="country"
                value={
                  formData.country ?? ''
                }
                label="Country"
                onChange={handleChange}
                required
              >
                <MenuItem value="">
                  <em>
                    Select Country
                  </em>
                </MenuItem>

                {countryOptions
                  .filter(Boolean)
                  .map((country) => (
                    <MenuItem
                      key={String(
                        country.countryId
                      )}
                      value={String(
                        country.countryId
                      )}
                    >
                      {
                        country.countryName
                      }
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            {/* State */}
            <FormControl
              fullWidth
              sx={{ mb: 3 }}
              required
            >
              <InputLabel id="state-label">
                State
              </InputLabel>

              <Select
                labelId="state-label"
                name="state"
                value={
                  formData.state ?? ''
                }
                label="State"
                onChange={handleChange}
                required
                disabled={
                  !formData.country
                }
              >
                <MenuItem value="">
                  <em>
                    Select State
                  </em>
                </MenuItem>

                {stateOptions
                  .filter(Boolean)
                  .map((state) => (
                    <MenuItem
                      key={String(
                        state.stateId
                      )}
                      value={String(
                        state.stateId
                      )}
                    >
                      {state.stateName}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            {/* City */}
            <FormControl
              fullWidth
              sx={{ mb: 3 }}
              required
            >
              <InputLabel id="city-label">
                City
              </InputLabel>

              <Select
                labelId="city-label"
                name="city"
                value={
                  formData.city ?? ''
                }
                label="City"
                onChange={handleChange}
                required
                disabled={
                  !formData.state
                }
              >
                <MenuItem value="">
                  <em>
                    Select City
                  </em>
                </MenuItem>

                {cityOptions
                  .filter(Boolean)
                  .map((city) => (
                    <MenuItem
                      key={String(city.cityId)}
                      value={String(
                        city.cityId
                      )}
                    >
                      {city.cityName}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            {/* Address */}
            <TextField
              label="Address"
              variant="outlined"
              fullWidth
              name="address"
              value={
                formData.address ?? ''
              }
              onChange={handleChange}
              sx={{ mb: 3 }}
            />

            {/* Marital status */}
            <FormControl
              fullWidth
              sx={{ mb: 3 }}
              required
            >
              <InputLabel id="marital-label">
                Marital Status
              </InputLabel>

              <Select
                labelId="marital-label"
                name="maritalStatus"
                value={
                  formData.maritalStatus ?? ''
                }
                label="Marital Status *"
                onChange={handleChange}
                required
              >
                <MenuItem value="">
                  <em>
                    Select Marital Status
                  </em>
                </MenuItem>

                {MARITAL_STATUS
                  .filter(Boolean)
                  .map((status) => (
                    <MenuItem
                      key={status.statusId}
                      value={
                        status.statusName
                      }
                    >
                      {status.statusName
                        .charAt(0)
                        .toUpperCase() +
                        status.statusName.slice(
                          1
                        )}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            {/* Resume */}
            <Box
              sx={{
                mb: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Button
                variant="contained"
                component="label"
                startIcon={
                  <AttachFileIcon />
                }
              >
                Choose Resume

                <input
                  type="file"
                  hidden
                  accept=".pdf,.docx"
                  onChange={
                    handleResumeChange
                  }
                />
              </Button>

              {resumeFileName && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color:
                        theme.palette.text
                          .primary,
                    }}
                  >
                    {resumeFileName}
                  </Typography>

                  <IconButton
                    onClick={
                      handleRemoveResume
                    }
                    size="small"
                    color="error"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>

            <Typography
              variant="caption"
              sx={{
                color:
                  theme.palette.text.primary,
                fontWeight: 500,
                ml: 1,
                display: 'block',
                mb: 3,
              }}
            >
              Maximum file size: 1MB
            </Typography>

            {/* Actions */}
            <Box
              sx={{
                display: 'flex',
                justifyContent:
                  'flex-end',
                gap: 2,
              }}
            >
              <Tooltip
                title={submitTooltip}
                arrow
              >
                <span>
                  <Button
                    variant="contained"
                    type="submit"
                    disabled={
                      !canSubmit || saving
                    }
                  >
                    {saving
                      ? 'UPDATING...'
                      : 'UPDATE'}
                  </Button>
                </span>
              </Tooltip>

              <Button
                variant="outlined"
                color="error"
                onClick={handleCancel}
                disabled={saving}
              >
                CANCEL
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() =>
          setOpenSnackbar(false)
        }
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Alert
          onClose={() =>
            setOpenSnackbar(false)
          }
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default EditEmployee;